import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import { format, getDay } from 'date-fns';
import { Plus, X, Clock, Check, MapPin, Plane } from 'lucide-react';

const AvailabilityCalendar = () => {
  const { user, token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [daySlots, setDaySlots] = useState([]);
  const [isFullDayOff, setIsFullDayOff] = useState(false);
  const [googleCal, setGoogleCal] = useState({ connected: false, configured: true });

  const [travelLocations, setTravelLocations] = useState([]);
  const [newTravelLocation, setNewTravelLocation] = useState({ location: '', city: '', start_date: '', end_date: '' });

  const [recurringDays, setRecurringDays] = useState([]);
  const daysOfWeek = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' }
  ];

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const config = { withCredentials: true, headers };

  const timeSlots = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00'];

  useEffect(() => {
    fetchAvailability();
    fetchProfileData();
  }, [currentMonth]);

  useEffect(() => {
    fetchGoogleStatus();
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get('calendar');
    if (c === 'connected') {
      toast.success('Google Calendar connected');
      window.history.replaceState({}, '', '/availability');
    } else if (c === 'error') {
      toast.error('Could not connect Google Calendar');
      window.history.replaceState({}, '', '/availability');
    }
  }, []);

  const fetchAvailability = async () => {
    if (!user) return;
    try {
      const monthStr = format(currentMonth, 'yyyy-MM');
      const response = await axios.get(`${API}/availability/${user.user_id}?month=${monthStr}`, config);
      setAvailability(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGoogleStatus = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/calendar/google/status`, config);
      setGoogleCal(response.data);
    } catch (error) {
      // non-fatal — sync just stays disconnected
    }
  };

  const connectGoogleCal = async () => {
    try {
      const response = await axios.get(`${API}/calendar/google/connect`, config);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google Calendar sync is not available yet');
    }
  };

  const disconnectGoogleCal = async () => {
    try {
      await axios.post(`${API}/calendar/google/disconnect`, {}, config);
      setGoogleCal({ connected: false, configured: true });
      toast.success('Google Calendar disconnected');
      fetchAvailability();
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  const [syncing, setSyncing] = useState(false);
  const syncGoogleNow = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/calendar/google/sync`, {}, config);
      setGoogleCal(g => ({ ...g, last_sync_at: response.data.last_sync_at }));
      await fetchAvailability();
      toast.success('Synced with Google Calendar');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync');
    } finally {
      setSyncing(false);
    }
  };

  const fetchProfileData = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/creatives/user/${user.user_id}`, config);
      setTravelLocations(response.data.travel_locations || []);
      setRecurringDays(response.data.recurring_unavailable_days || []);
    } catch (error) {
      console.error('Error fetching profile data:', error);
    }
  };

  // "14:30" -> "2:30 PM"
  const formatTime12 = (t) => {
    if (!t) return t;
    const [hStr, m] = t.split(':');
    let h = parseInt(hStr, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  };

  const getDateStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const slots = availability.filter(s => s.date === dateStr);
    const dayOfWeek = (getDay(date) + 6) % 7;
    if (recurringDays.includes(dayOfWeek)) return 'recurring';
    if (slots.length === 0) return 'available';
    if (slots.some(s => s.booking_id)) return 'booked';
    if (slots.some(s => !s.start_time && !s.is_available)) return 'unavailable';
    if (slots.some(s => !s.is_available)) return 'partial';
    return 'available';
  };

  // Blocks for a given date, for the day-detail panel below the calendar
  const getDayBlocks = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayOfWeek = (getDay(date) + 6) % 7;
    const slots = availability.filter(s => s.date === dateStr);
    const booking = slots.find(s => s.booking_id);
    const fullDayOff = slots.find(s => !s.start_time && !s.is_available && !s.booking_id);
    const timed = slots
      .filter(s => s.start_time && !s.is_available && !s.booking_id)
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
    return {
      recurring: recurringDays.includes(dayOfWeek),
      booking,
      fullDayOff: !!fullDayOff,
      fullDayGoogle: fullDayOff?.source === 'google',
      timed,
    };
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingSlots = availability.filter(s => s.date === dateStr && !s.booking_id && s.source !== 'google');
    if (existingSlots.length > 0) {
      const fullDayOff = existingSlots.some(s => !s.start_time && !s.is_available);
      setIsFullDayOff(fullDayOff);
      if (!fullDayOff) {
        setDaySlots(existingSlots.map(s => ({ start_time: s.start_time, end_time: s.end_time, is_available: s.is_available })));
      } else {
        setDaySlots([]);
      }
    } else {
      setIsFullDayOff(false);
      setDaySlots([]);
    }
    setEditOpen(true);
  };

  const addTimeSlot = () => {
    setDaySlots([...daySlots, { start_time: '09:00', end_time: '17:00', is_available: false }]);
    setIsFullDayOff(false);
  };

  const removeTimeSlot = (index) => setDaySlots(daySlots.filter((_, i) => i !== index));

  const updateTimeSlot = (index, field, value) => {
    const updated = [...daySlots];
    updated[index][field] = value;
    setDaySlots(updated);
  };

  const handleSave = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      let slots = [];
      if (isFullDayOff) slots = [{ full_day: true, is_available: false }];
      else if (daySlots.length > 0) slots = daySlots;
      await axios.post(`${API}/availability`, { date: dateStr, slots }, config);
      toast.success('Availability updated');
      setEditOpen(false);
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update availability');
    }
  };

  const handleClearDay = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.post(`${API}/availability`, { date: dateStr, slots: [] }, config);
      toast.success('Day cleared - now available');
      setEditOpen(false);
      fetchAvailability();
    } catch (error) {
      toast.error('Failed to clear day');
    }
  };

  const addTravelLocation = async () => {
    if (!newTravelLocation.location || !newTravelLocation.city || !newTravelLocation.start_date || !newTravelLocation.end_date) {
      toast.error('Please fill in all travel location fields'); return;
    }
    if (new Date(newTravelLocation.end_date) < new Date(newTravelLocation.start_date)) {
      toast.error('End date must be after start date'); return;
    }
    try {
      const updatedLocations = [...travelLocations, { ...newTravelLocation }];
      await axios.put(`${API}/creatives/profile`, { travel_locations: updatedLocations }, config);
      setTravelLocations(updatedLocations);
      setNewTravelLocation({ location: '', city: '', start_date: '', end_date: '' });
      toast.success('Travel location added');
    } catch (error) {
      toast.error('Failed to add travel location');
    }
  };

  const removeTravelLocation = async (index) => {
    try {
      const updatedLocations = travelLocations.filter((_, i) => i !== index);
      await axios.put(`${API}/creatives/profile`, { travel_locations: updatedLocations }, config);
      setTravelLocations(updatedLocations);
      toast.success('Travel location removed');
    } catch (error) {
      toast.error('Failed to remove travel location');
    }
  };

  const toggleRecurringDay = async (dayValue) => {
    try {
      const updatedDays = recurringDays.includes(dayValue)
        ? recurringDays.filter(d => d !== dayValue)
        : [...recurringDays, dayValue];
      await axios.put(`${API}/creatives/profile`, { recurring_unavailable_days: updatedDays }, config);
      setRecurringDays(updatedDays);
      toast.success('Recurring availability updated');
    } catch (error) {
      toast.error('Failed to update recurring availability');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f5f2]">
        <div className="animate-pulse text-[#a0a0a0] text-sm">Loading calendar...</div>
      </div>
    );
  }

  const dayBlocks = selectedDate ? getDayBlocks(selectedDate) : null;
  const googleDaySlots = selectedDate
    ? availability.filter(s => s.source === 'google' && s.date === format(selectedDate, 'yyyy-MM-dd'))
    : [];

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Header */}
      <div className="bg-white border-b border-[#e4e1db] px-5 md:px-10 pt-7 pb-5">
        <div className="max-w-[1140px] mx-auto flex items-end justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-1.5">Manage</p>
            <h1 className="font-display font-normal text-[28px] md:text-[32px] text-[#0e0e0e] tracking-[-0.5px] leading-[1.1]">Availability Calendar</h1>
          </div>
          <button
            onClick={handleSave}
            className="inline-flex items-center text-[13px] font-medium px-5 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity mb-1"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-5 md:px-10 py-6 max-w-[1140px] mx-auto">

        {/* Legend */}
        <div className="flex gap-4 flex-wrap mb-5">
          <div className="flex items-center gap-2 text-[12px] text-[#5a5a5a]">
            <div className="w-[10px] h-[10px] rounded-[3px] bg-white border-[1.5px] border-[#c8c3bb] flex-shrink-0" />
            Available
          </div>
          <div className="flex items-center gap-2 text-[12px] text-[#5a5a5a]">
            <div className="w-[10px] h-[10px] rounded-[3px] bg-[#0e0e0e] flex-shrink-0" />
            Booked
          </div>
          <div className="flex items-center gap-2 text-[12px] text-[#5a5a5a]">
            <div className="w-[10px] h-[10px] rounded-[3px] flex-shrink-0" style={{ background: '#f87171', opacity: 0.5 }} />
            Day Off
          </div>
          <div className="flex items-center gap-2 text-[12px] text-[#5a5a5a]">
            <div className="w-[10px] h-[10px] rounded-[3px] flex-shrink-0" style={{ background: '#a0a0a0', opacity: 0.4 }} />
            Recurring Day Off
          </div>
          <div className="flex items-center gap-2 text-[12px] text-[#5a5a5a]">
            <div className="w-[10px] h-[10px] rounded-[3px] flex-shrink-0" style={{ background: '#d4a843', opacity: 0.6 }} />
            Partially Unavailable
          </div>
        </div>

        {/* 2-col grid */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">

          {/* Calendar */}
          <div className="bg-white border border-[#e4e1db] rounded-[24px] overflow-hidden w-full">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && handleDateClick(date)}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              className="w-full p-4"
              classNames={{
                months: 'w-full',
                month: 'w-full',
                caption: 'flex items-center justify-between mb-4 px-1',
                caption_label: 'font-display font-normal text-[22px] text-[#0e0e0e]',
                nav: 'flex gap-1.5',
                nav_button: 'w-[32px] h-[32px] rounded-full border border-[#e4e1db] bg-white flex items-center justify-center text-[13px] text-[#5a5a5a] hover:bg-[#f7f5f2] transition-colors',
                nav_button_previous: '',
                nav_button_next: '',
                table: 'w-full',
                head_row: 'grid grid-cols-7 mb-2',
                head_cell: 'text-center text-[11px] font-bold tracking-[1px] uppercase text-[#a0a0a0] py-1',
                row: 'grid grid-cols-7 gap-1 mb-1',
                cell: 'h-[64px] flex items-center justify-center',
                day: 'w-full h-full flex items-center justify-center rounded-[12px] text-[15px] font-medium text-[#5a5a5a] cursor-pointer hover:bg-[#f7f5f2] transition-colors',
                day_selected: 'bg-[#0e0e0e] text-white hover:bg-[#0e0e0e]',
                day_today: 'bg-[#0e0e0e] text-white rounded-full font-semibold',
                day_outside: 'text-[#a0a0a0] opacity-40',
              }}
              modifiers={{
                booked:      (date) => getDateStatus(date) === 'booked',
                unavailable: (date) => getDateStatus(date) === 'unavailable',
                recurring:   (date) => getDateStatus(date) === 'recurring',
                partial:     (date) => getDateStatus(date) === 'partial',
              }}
              modifiersClassNames={{
                booked:      '!bg-[#0e0e0e] !text-white !opacity-70',
                unavailable: '!bg-[#fef2f2] !text-[#dc2626]',
                recurring:   '!bg-[#f7f5f2] !text-[#a0a0a0]',
                partial:     '!bg-[#f5ead0] !text-[#8a6a1a]',
              }}
            />

            {/* Selected-day detail */}
            <div className="border-t border-[#e4e1db] px-4 py-3.5">
              {dayBlocks ? (
                <>
                  <p className="text-[12px] font-semibold text-[#0e0e0e] mb-0.5">
                    {format(selectedDate, 'EEEE, MMMM d')}
                  </p>
                  <p className="text-[10px] text-[#a0a0a0] mb-2">Times shown in Mountain Time</p>
                  {dayBlocks.booking ? (
                    <p className="text-[12px] text-[#5a5a5a]">
                      Booked{dayBlocks.booking.start_time ? ` from ${formatTime12(dayBlocks.booking.start_time)}` : ''} — blocked by a confirmed booking.
                    </p>
                  ) : dayBlocks.recurring ? (
                    <p className="text-[12px] text-[#5a5a5a]">Recurring day off — never available this weekday.</p>
                  ) : dayBlocks.fullDayOff ? (
                    <p className="text-[12px] text-[#5a5a5a]">
                      {dayBlocks.fullDayGoogle ? 'Busy all day on Google Calendar.' : 'Marked unavailable all day.'}
                    </p>
                  ) : dayBlocks.timed.length > 0 ? (
                    <ul className="space-y-1.5">
                      {dayBlocks.timed.map((s, i) => (
                        <li key={i} className="flex items-center gap-2 text-[12px] text-[#5a5a5a]">
                          <Clock className="w-3.5 h-3.5 text-[#a0a0a0] flex-shrink-0" />
                          <span className="font-medium text-[#0e0e0e]">
                            {formatTime12(s.start_time)}{s.end_time ? ` – ${formatTime12(s.end_time)}` : ''}
                          </span>
                          <span>{s.source === 'google' ? 'busy on Google Calendar' : 'unavailable'}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[12px] text-[#a0a0a0]">Available all day. Click the date to block times.</p>
                  )}
                </>
              ) : (
                <p className="text-[12px] text-[#a0a0a0] text-center">Click a date to see or set your availability.</p>
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-4">
            {/* Google Calendar sync */}
            <div className="bg-white border border-[#e4e1db] rounded-[24px] p-[22px]">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="w-[8px] h-[8px] rounded-full flex-shrink-0"
                  style={{ background: googleCal.connected ? '#4ade80' : '#c8c3bb' }}
                />
                <p className="text-[13px] font-semibold text-[#0e0e0e]">Google Calendar</p>
              </div>
              {googleCal.connected ? (
                <>
                  <p className="text-[12px] text-[#5a5a5a] mb-1">
                    Busy times from{googleCal.email ? ` ${googleCal.email}` : ' your calendar'} show here as unavailable, and confirmed bookings are sent to your calendar.
                  </p>
                  <p className="text-[11px] text-[#a0a0a0] mb-4">
                    {googleCal.last_sync_at
                      ? `Last synced ${format(new Date(googleCal.last_sync_at), 'MMM d, h:mm a')}`
                      : 'Not synced yet'}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={syncGoogleNow}
                      disabled={syncing}
                      className="text-[12px] font-medium px-3.5 py-1.5 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity disabled:opacity-50"
                    >
                      {syncing ? 'Syncing…' : 'Sync now'}
                    </button>
                    <button
                      onClick={disconnectGoogleCal}
                      className="text-[12px] font-medium px-3.5 py-1.5 rounded-full border border-[#e4e1db] bg-white text-[#5a5a5a] hover:border-[#0e0e0e] hover:text-[#0e0e0e] transition-all"
                    >
                      Disconnect
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[12px] text-[#5a5a5a] mb-4">
                    Connect your Google Calendar so your busy times block bookings automatically, and confirmed bookings land on your calendar.
                  </p>
                  <button
                    onClick={connectGoogleCal}
                    className="text-[12px] font-medium px-4 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity"
                  >
                    Connect Google Calendar
                  </button>
                </>
              )}
            </div>

            {/* Recurring Days Off */}
            <div className="bg-white border border-[#e4e1db] rounded-[24px] p-[22px]">
              <p className="text-[13px] font-semibold text-[#0e0e0e] mb-1">Recurring Days Off</p>
              <p className="text-[12px] text-[#5a5a5a] mb-4">Select days you're never available for bookings</p>
              <div className="flex gap-2 flex-wrap">
                {daysOfWeek.map((day) => (
                  <button
                    key={day.value}
                    onClick={() => toggleRecurringDay(day.value)}
                    className={`text-[12px] font-medium px-3.5 py-1.5 rounded-full border transition-all ${
                      recurringDays.includes(day.value)
                        ? 'bg-[#0e0e0e] text-white border-[#0e0e0e]'
                        : 'bg-white text-[#5a5a5a] border-[#e4e1db] hover:border-[#0e0e0e] hover:text-[#0e0e0e]'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            {/* How to use */}
            <div className="bg-white border border-[#e4e1db] rounded-[24px] p-[22px]">
              <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-3">How to use</p>
              <div className="space-y-2.5 text-[13px] text-[#5a5a5a] leading-relaxed">
                <div className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-0.5 text-[#4ade80] flex-shrink-0" />
                  Click any calendar date to toggle your availability
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-0.5 text-[#4ade80] flex-shrink-0" />
                  Recurring days off apply every week
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-0.5 text-[#4ade80] flex-shrink-0" />
                  Confirmed bookings automatically block your calendar
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Travel Schedule */}
        <div className="bg-white border border-[#e4e1db] rounded-[24px] p-[22px] mt-6">
          <div className="flex items-center gap-2 mb-1">
            <Plane className="w-4 h-4 text-[#5a5a5a]" />
            <p className="text-[13px] font-semibold text-[#0e0e0e]">Travel Schedule</p>
          </div>
          <p className="text-[12px] text-[#5a5a5a] mb-4">Add locations where you'll be available for shoots while traveling</p>

          {travelLocations.length > 0 && (
            <div className="space-y-2 mb-4">
              {travelLocations.map((travel, index) => (
                <div key={index} className="flex items-center justify-between bg-[#f7f5f2] border border-[#e4e1db] p-3 rounded-[14px]">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-[#a0a0a0]" />
                    <div>
                      <p className="text-[13px] font-semibold text-[#0e0e0e]">{travel.location}</p>
                      <p className="text-[11px] text-[#a0a0a0]">
                        {new Date(travel.start_date).toLocaleDateString()} – {new Date(travel.end_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => removeTravelLocation(index)} className="text-[#a0a0a0] hover:text-[#f87171] transition-colors p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="border border-[#e4e1db] rounded-[14px] p-4 bg-[#f7f5f2]">
            <p className="text-[13px] font-semibold text-[#0e0e0e] mb-3">Add Travel Location</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Location</Label>
                <Input value={newTravelLocation.location} onChange={e => setNewTravelLocation({ ...newTravelLocation, location: e.target.value })} placeholder="e.g., New York, NY" />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">City (for search)</Label>
                <Input value={newTravelLocation.city} onChange={e => setNewTravelLocation({ ...newTravelLocation, city: e.target.value })} placeholder="e.g., New York" />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Start Date</Label>
                <Input type="date" value={newTravelLocation.start_date} onChange={e => setNewTravelLocation({ ...newTravelLocation, start_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">End Date</Label>
                <Input type="date" value={newTravelLocation.end_date} onChange={e => setNewTravelLocation({ ...newTravelLocation, end_date: e.target.value })} />
              </div>
            </div>
            <button onClick={addTravelLocation} className="mt-3 inline-flex items-center gap-2 text-[13px] font-medium px-4 py-2 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all">
              <Plus className="w-4 h-4" /> Add Travel Location
            </button>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-normal text-[22px]">
              {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Full Day Toggle */}
            <div className="flex items-center justify-between p-4 rounded-[14px] bg-[#f7f5f2] border border-[#e4e1db]">
              <span className="text-[13px] font-semibold text-[#0e0e0e]">Mark entire day as unavailable</span>
              <button
                onClick={() => { setIsFullDayOff(!isFullDayOff); if (!isFullDayOff) setDaySlots([]); }}
                className={`w-12 h-6 rounded-full transition-colors ${isFullDayOff ? 'bg-[#f87171]' : 'bg-[#e4e1db]'}`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${isFullDayOff ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Time Slots */}
            {!isFullDayOff && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[13px] font-semibold text-[#0e0e0e]">Unavailable time slots</span>
                  <button onClick={addTimeSlot} className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all">
                    <Plus className="w-3.5 h-3.5" /> Add Slot
                  </button>
                </div>
                {daySlots.length > 0 ? (
                  <div className="space-y-2">
                    {daySlots.map((slot, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 rounded-[14px] bg-[#f7f5f2] border border-[#e4e1db]">
                        <Clock className="w-4 h-4 text-[#a0a0a0]" />
                        <Select value={slot.start_time} onValueChange={v => updateTimeSlot(index, 'start_time', v)}>
                          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>{timeSlots.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-[12px] text-[#5a5a5a]">to</span>
                        <Select value={slot.end_time} onValueChange={v => updateTimeSlot(index, 'end_time', v)}>
                          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>{timeSlots.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                        <button onClick={() => removeTimeSlot(index)} className="ml-auto text-[#a0a0a0] hover:text-[#f87171] transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[12px] text-[#a0a0a0] text-center py-4">
                    {googleDaySlots.length > 0
                      ? 'No blocks you set here — see Google Calendar times below.'
                      : 'No time blocks set. This day is fully available.'}
                  </p>
                )}
              </div>
            )}

            {/* Google Calendar busy times (read-only) */}
            {!isFullDayOff && googleDaySlots.length > 0 && (
              <div>
                <span className="text-[13px] font-semibold text-[#0e0e0e]">From Google Calendar</span>
                <p className="text-[11px] text-[#a0a0a0] mb-2">Managed in Google — edit them there, not here. Times shown in Mountain Time.</p>
                <div className="space-y-2">
                  {googleDaySlots.map((slot, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 rounded-[14px] bg-[#f7f5f2] border border-[#e4e1db] opacity-75">
                      <Clock className="w-4 h-4 text-[#a0a0a0]" />
                      <span className="text-[12px] text-[#5a5a5a]">
                        {slot.start_time ? `${formatTime12(slot.start_time)} – ${formatTime12(slot.end_time) || ''}` : 'Busy all day'}
                      </span>
                      <span className="ml-auto text-[11px] text-[#a0a0a0]">busy</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button onClick={handleClearDay} className="flex-1 text-[13px] font-medium py-2.5 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all">
                Clear Day
              </button>
              <button onClick={handleSave} className="flex-1 text-[13px] font-medium py-2.5 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity">
                Save Changes
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AvailabilityCalendar;
